/**
 * MatrixOS / Morpheus browser storage engine.
 *
 * localStorage is reserved for small synchronous identity/preferences.
 * IndexedDB stores large/cache/history payloads. Firestore remains authoritative.
 */
const DB_NAME = "matrixos-morpheus";
const DB_VERSION = 2;
const VALUE_STORE = "values";
const META_STORE = "meta";
const MIGRATION_META_KEY = "localStorageMigration.v2";
const ENGINE_META_KEY = "storageEngine.v2";
const LARGE_VALUE_BYTES = 128 * 1024;
const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MEMORY_CACHE = new Map();

const LARGE_CACHE_PATTERNS = [
  /^mc\.inventoryLearningHistory\./i,
  /^mc-(?:logic-pass|engine|import-active)(?::|_|$)/i,
  /^mc-active-inventory/i,
  /^mc-prepared/i,
  /^mc-product-intelligence/i,
  /^mc-inventory-(?:snapshot|exceptions|patterns|readiness|history)/i,
  /^mc-session-detail/i,
  /^mc-archive-detail/i,
  /^mc-session-engine/i,
  /^mc-count-ledger/i,
  /^mc-session-archive$/i,
  /^mc-session-list$/i,
  /^mc-sites$/i,
  /^__mc_session_/i,
];

const PROTECTED_SMALL_KEYS = [
  /^firebase:/i,
  /^mc-theme$/i,
  /^morpheus-theme$/i,
  /^matrixos-mc-theme$/i,
  /^mc-active-org/i,
  /^mc-active-org-id/i,
  /^mc-current-profile/i,
  // MC477 — the profile directory is small, durable identity authority. Never
  // classify it as disposable browser cache even if the profile list grows.
  /^mc-profiles$/i,
  /^mc-admin-store/i,
  /^mc-primary-owner-email/i,
  /^mc-access-/i,
];

function canUseIndexedDb() {
  return typeof window !== "undefined" && !!window.indexedDB;
}

function openDatabase() {
  if (!canUseIndexedDb()) return Promise.reject(new Error("IndexedDB is unavailable"));
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(VALUE_STORE)) {
        const values = db.createObjectStore(VALUE_STORE, { keyPath: "key" });
        values.createIndex("updatedAtMs", "updatedAtMs", { unique: false });
        values.createIndex("expiresAtMs", "expiresAtMs", { unique: false });
      } else {
        const values = request.transaction.objectStore(VALUE_STORE);
        if (!values.indexNames.contains("updatedAtMs")) values.createIndex("updatedAtMs", "updatedAtMs", { unique: false });
        if (!values.indexNames.contains("expiresAtMs")) values.createIndex("expiresAtMs", "expiresAtMs", { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Could not open IndexedDB"));
  });
}

async function withStore(storeName, mode, action) {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);
      let request;
      try { request = action(store); } catch (error) { reject(error); return; }
      if (request && typeof request === "object" && "onsuccess" in request) {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || transaction.error);
      } else {
        transaction.oncomplete = () => resolve(request);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error || new Error("IndexedDB transaction aborted"));
      }
    });
  } finally {
    db.close();
  }
}

function normalizeStoredValue(value) {
  return typeof value === "string" ? value : JSON.stringify(value);
}

export function approximateStorageBytes(value) {
  return String(value ?? "").length * 2;
}

export function isLargeMorpheusCacheKey(key) {
  const value = String(key || "");
  return LARGE_CACHE_PATTERNS.some((pattern) => pattern.test(value));
}

export function shouldUseIndexedDb(key, value = "") {
  return isLargeMorpheusCacheKey(key) || approximateStorageBytes(value) >= LARGE_VALUE_BYTES;
}

function isProtectedSmallKey(key) {
  const value = String(key || "");
  return PROTECTED_SMALL_KEYS.some((pattern) => pattern.test(value));
}

function removeLocalCacheOnly(key) {
  if (typeof window === "undefined" || !window.localStorage) return;
  const previous = window.__MC_SUPPRESS_CLOUD_DELETE__;
  window.__MC_SUPPRESS_CLOUD_DELETE__ = true;
  try {
    window.localStorage.removeItem(String(key));
  } finally {
    if (previous === undefined) {
      try { delete window.__MC_SUPPRESS_CLOUD_DELETE__; } catch (_) {
        window.__MC_SUPPRESS_CLOUD_DELETE__ = false;
      }
    } else {
      window.__MC_SUPPRESS_CLOUD_DELETE__ = previous;
    }
  }
}

export async function idbGetItem(key) {
  const stringKey = String(key);
  if (!canUseIndexedDb()) return MEMORY_CACHE.get(stringKey) ?? null;
  try {
    const row = await withStore(VALUE_STORE, "readonly", (store) => store.get(stringKey));
    if (!row) return null;
    if (row.expiresAtMs && row.expiresAtMs <= Date.now()) {
      idbRemoveItem(stringKey).catch(() => {});
      return null;
    }
    return row.value ?? null;
  } catch (_) {
    return MEMORY_CACHE.get(stringKey) ?? null;
  }
}

export async function idbSetItem(key, value, metadata = {}) {
  const stringKey = String(key);
  const stringValue = normalizeStoredValue(value);
  MEMORY_CACHE.set(stringKey, stringValue);
  if (!canUseIndexedDb()) return stringValue;
  const updatedAtMs = Date.now();
  const ttlMs = Number(metadata.ttlMs ?? DEFAULT_TTL_MS);
  const row = {
    key: stringKey,
    value: stringValue,
    bytes: approximateStorageBytes(stringValue),
    updatedAt: new Date(updatedAtMs).toISOString(),
    updatedAtMs,
    expiresAtMs: ttlMs > 0 ? updatedAtMs + ttlMs : 0,
    ...metadata,
  };
  await withStore(VALUE_STORE, "readwrite", (store) => store.put(row));
  return stringValue;
}

export async function idbRemoveItem(key) {
  const stringKey = String(key);
  MEMORY_CACHE.delete(stringKey);
  if (!canUseIndexedDb()) return;
  await withStore(VALUE_STORE, "readwrite", (store) => store.delete(stringKey));
}

export async function idbGetJson(key, fallback = null) {
  try {
    const raw = await idbGetItem(key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

export async function idbSetJson(key, value, metadata = {}) {
  return idbSetItem(key, JSON.stringify(value), metadata);
}

export async function idbListItems() {
  if (!canUseIndexedDb()) {
    return Array.from(MEMORY_CACHE.entries()).map(([key, value]) => ({ key, value, bytes: approximateStorageBytes(value) }));
  }
  try {
    const rows = await withStore(VALUE_STORE, "readonly", (store) => store.getAll());
    const now = Date.now();
    return (Array.isArray(rows) ? rows : []).filter((row) => !row.expiresAtMs || row.expiresAtMs > now);
  } catch (_) {
    return [];
  }
}

export async function hydrateIndexedMorpheusCache() {
  const rows = await idbListItems();
  rows.forEach((row) => MEMORY_CACHE.set(String(row.key), String(row.value ?? "")));
  return rows;
}

function localStorageCandidates() {
  if (typeof window === "undefined" || !window.localStorage) return [];
  const rows = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index) || "";
    if (!key || isProtectedSmallKey(key)) continue;
    let value = "";
    try { value = window.localStorage.getItem(key) || ""; } catch (_) {}
    if (!value) continue;
    if (shouldUseIndexedDb(key, value)) rows.push({ key, value, bytes: approximateStorageBytes(value) });
  }
  return rows.sort((a, b) => b.bytes - a.bytes);
}

export function freeLocalStorageForCriticalWrite(minimumBytes = 1024 * 1024) {
  if (typeof window === "undefined" || !window.localStorage) return { removed: 0, bytes: 0, keys: [] };
  const candidates = localStorageCandidates();
  let bytes = 0;
  const keys = [];
  candidates.some(({ key, value, bytes: candidateBytes }) => {
    idbSetItem(key, value, { migratedFrom: "localStorage", reason: "quota-recovery" }).catch(() => {});
    try {
      removeLocalCacheOnly(key);
      bytes += candidateBytes;
      keys.push(key);
    } catch (_) {}
    return bytes >= minimumBytes;
  });
  return { removed: keys.length, bytes, keys };
}

export async function migrateLargeLocalStorageCaches({ removeAfterCopy = true } = {}) {
  const candidates = localStorageCandidates();
  const migrated = [];
  const failed = [];
  for (const item of candidates) {
    try {
      await idbSetItem(item.key, item.value, { migratedFrom: "localStorage", reason: "storage-engine-v2" });
      if (removeAfterCopy) {
        try { removeLocalCacheOnly(item.key); } catch (_) {}
      }
      migrated.push({ key: item.key, bytes: item.bytes });
    } catch (error) {
      failed.push({ key: item.key, error: String(error?.message || error) });
    }
  }
  try {
    await withStore(META_STORE, "readwrite", (store) => store.put({
      key: MIGRATION_META_KEY,
      completedAt: new Date().toISOString(),
      migratedCount: migrated.length,
      migratedBytes: migrated.reduce((sum, row) => sum + row.bytes, 0),
      failed,
    }));
  } catch (_) {}
  return { migrated, failed };
}

export async function cleanupMorpheusIndexedCache({ maxAgeMs = DEFAULT_TTL_MS, maxBytes = 80 * 1024 * 1024 } = {}) {
  const rows = await idbListItems();
  const now = Date.now();
  const stale = rows.filter((row) => {
    const updatedAtMs = Number(row.updatedAtMs || Date.parse(row.updatedAt || "") || 0);
    return (row.expiresAtMs && row.expiresAtMs <= now) || (updatedAtMs && now - updatedAtMs > maxAgeMs);
  });
  for (const row of stale) await idbRemoveItem(row.key);

  const remaining = (await idbListItems()).sort((a, b) => Number(a.updatedAtMs || 0) - Number(b.updatedAtMs || 0));
  let totalBytes = remaining.reduce((sum, row) => sum + Number(row.bytes || approximateStorageBytes(row.value)), 0);
  const evicted = [];
  for (const row of remaining) {
    if (totalBytes <= maxBytes) break;
    await idbRemoveItem(row.key);
    const bytes = Number(row.bytes || approximateStorageBytes(row.value));
    totalBytes -= bytes;
    evicted.push({ key: row.key, bytes });
  }
  return { staleRemoved: stale.length, evicted, totalBytes };
}

export async function initializeMorpheusStorageEngine() {
  if (!canUseIndexedDb()) return { ready: false, reason: "indexeddb-unavailable", fallback: "memory" };
  await openDatabase().then((db) => db.close());
  const migrated = await migrateLargeLocalStorageCaches({ removeAfterCopy: true });
  const rows = await hydrateIndexedMorpheusCache();
  const cleanup = await cleanupMorpheusIndexedCache();
  try {
    await withStore(META_STORE, "readwrite", (store) => store.put({
      key: ENGINE_META_KEY,
      initializedAt: new Date().toISOString(),
      hydratedCount: rows.length,
      migratedCount: migrated.migrated.length,
      cleanup,
    }));
  } catch (_) {}
  return { ready: true, database: DB_NAME, version: DB_VERSION, migrated, hydratedCount: rows.length, cleanup };
}

export const morpheusStorage = {
  getItem: idbGetItem,
  setItem: idbSetItem,
  removeItem: idbRemoveItem,
  getJson: idbGetJson,
  setJson: idbSetJson,
  listItems: idbListItems,
  hydrate: hydrateIndexedMorpheusCache,
  initialize: initializeMorpheusStorageEngine,
  migrateLargeLocalStorageCaches,
  cleanup: cleanupMorpheusIndexedCache,
  freeLocalStorageForCriticalWrite,
  shouldUseIndexedDb,
};
